import {
  Box,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue,
  Flex,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Heading,
  Tag,
  WrapItem,
  Wrap,
  Thead,
  Table,
  Th,
  Tr,
  Tbody,
  Td,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalOverlay,
  Input,
  Textarea,
  ModalFooter,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  CheckboxGroup,
  Stack,
  Checkbox,
  Container,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { ChatDrawer } from "../../components/chat/ChatDrawer";
import { ChatNavbar } from "../../components/chat/ChatNavbar";
import { useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { userAPI } from "services/user-api";
import { useChatStore } from "stores/chatStore";
import {
  ChatSessionType,
  TicketPriority,
  Character,
  AgentUsageMode,
} from "types";
import {
  FaComments,
  FaUser,
  FaRobot,
  FaUsers,
  FaCreditCard,
  FaDollarSign,
  FaDiscord,
  FaUserSecret,
  FaCubes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  FiCode,
  FiColumns,
  FiCopy,
  FiCreditCard,
  FiDollarSign,
  FiEdit,
  FiEye,
  FiFileText,
  FiGrid,
  FiLifeBuoy,
  FiList,
  FiMail,
  FiMessageSquare,
  FiPhoneCall,
  FiPlus,
  FiSettings,
  FiTrash,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { SubUser } from "types";
import { subUsersAPI } from "services/subuser-api";
import { Footer } from "components/layout/Footer";
interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const Subuser = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isOpenNewSubUser, setIsOpenNewSubUser] =
    React.useState<boolean>(false);
  const [credits, setCredits] = React.useState<number>(0);
  const [subUsers, setSubUsers] = React.useState<SubUser[]>([]);
  const toast = useToast();
  const navigate = useNavigate();
  const {
    currentSession,
    sessions,
    loadSessions,
    createSession,
    deleteSession,
    updateSessionName,
  } = useChatStore();
  const { colorMode } = useColorMode();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [sessionNameInput, setSessionNameInput] = React.useState<string>("");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const [newSubUserName, setNewSubUserName] = React.useState<string>("");
  const [newSubUserEmail, setNewSubUserEmail] = React.useState<string>("");
  const [newSubUserPassword, setNewSubUserPassword] =
    React.useState<string>("");
  const [newSubUserPhone, setNewSubUserPhone] = React.useState<string>("");
  const loadSubUsers = async () => {
    try {
      const subUsers = await subUsersAPI.getSubUsers(page, limit);
      setSubUsers(subUsers.subUsers);
      setTotalPages(subUsers.pagination.pages);
    } catch (error) {
      setError(t("subusers.error"));
      console.error("Error loading subusers:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const loadUserData = async () => {
    try {
      const userData = await userAPI.getCurrentUser();
      setCredits(Number(userData.credits));
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };
  useEffect(() => {
    loadSubUsers();
    loadUserData();
  }, []);

  useEffect(() => {
    loadSubUsers();
  }, [page]);

  const handleSelectSession = async (selectedSessionId: string) => {
    await navigate(`/chat/${selectedSessionId}`);
  };
  const handleStartRename = (
    session: { id: string; sessionName?: string },
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setSessionNameInput(session.sessionName || "");
  };
  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSessionId && sessionNameInput.trim()) {
      await updateSessionName(editingSessionId, sessionNameInput.trim());
      setEditingSessionId(null);
      setSessionNameInput("");
    }
  };
  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteSession(id);
  };
  const handleNewChat = async (
    sessionType: ChatSessionType = ChatSessionType.STANDARD
  ) => {
    const session = await createSession(sessionType);
    if (session) {
      navigate(`/chat/${session.id}`);
    }
    loadSessions();
  };
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleNewSubUser = async () => {
    setIsLoading(true);
    // validate if the email is valid
    if (!isValidEmail(newSubUserEmail)) {
      setError(t("subusers.invalidEmail"));
      toast({
        title: t("subusers.invalidEmail"),
        description: t("subusers.invalidEmailDescription"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }
    const subUser = await subUsersAPI.createSubUser({
      name: newSubUserName,
      email: newSubUserEmail,
      password: newSubUserPassword,
      phone: newSubUserPhone,
    });
    if (subUser) {
      setIsOpenNewSubUser(false);
      loadSubUsers();
    }
    setIsLoading(false);
  };
  const handleDeleteSubUser = async (id: string) => {
    await subUsersAPI.deleteSubUser(id);
    loadSubUsers();
  };
  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <ChatNavbar
        onOpen={onOpen}
        navigate={navigate}
        credits={credits}
        colorMode={colorMode}
        borderColor={borderColor}
      />
      <ChatDrawer
        isOpen={isOpen}
        onClose={onClose}
        sessions={sessions}
        handleNewChat={handleNewChat}
        handleSelectSession={handleSelectSession}
        handleRename={handleRename}
        handleDeleteSession={handleDeleteSession}
        editingSessionId={editingSessionId}
        sessionNameInput={sessionNameInput}
        setSessionNameInput={setSessionNameInput}
        useColorModeValue={useColorModeValue}
        setEditingSessionId={setEditingSessionId}
        handleStartRename={handleStartRename}
        currentSession={currentSession}
      />

      {/* <Box flex="1" bg={bgColor} py={20}> */}
      <Flex flex={1} p={8} align="center" justify="center" bg={bgColor} mt={10}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("subusers.title")}
              </Heading>
              <Text color="gray.500">{t("subusers.subtitle")}</Text>
            </VStack>

            {/* Subusers Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <HStack w="full" justify="space-between">
                <Heading size="md">{t("subusers.title")}</Heading>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="brand"
                  onClick={() => setIsOpenNewSubUser(true)}
                >
                  {t("subusers.create")}
                </Button>
              </HStack>

              <Divider />

              {/* Subusers Table */}
              <Box overflowX="auto" w="100%">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t("subusers.table.title")}</Th>
                      <Th>{t("subusers.table.email")}</Th>
                      <Th>{t("subusers.table.phone")}</Th>
                      <Th>{t("subusers.table.actions")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {subUsers.map((subUser) => (
                      <Tr key={subUser.id}>
                        <Td>
                          <VStack>
                            <Text fontWeight="bold">{subUser.name}</Text>
                            <Badge colorScheme="gray" ml={2}>
                              {subUser.email}
                            </Badge>
                          </VStack>
                        </Td>
                        <Td>{subUser.email}</Td>
                        <Td>
                          <Wrap>{subUser.phone}</Wrap>
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label={t("subusers.actions.edit")}>
                              <IconButton
                                aria-label="Edit subuser"
                                icon={<FiEdit />}
                                size="sm"
                                onClick={() =>
                                  navigate(`/subusers/${subUser.id}`)
                                }
                              />
                            </Tooltip>
                            <Tooltip label={t("subusers.actions.delete")}>
                              <IconButton
                                aria-label="Delete subuser"
                                icon={<FiTrash />}
                                size="sm"
                                onClick={() => handleDeleteSubUser(subUser.id)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Add pagination controls */}
              <HStack spacing={2} justify="space-between" w="full">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                >
                  {t("common.previous")}
                </Button>
                <Text>
                  {t("common.page")} {page} / {totalPages}
                </Text>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page === totalPages}
                >
                  {t("common.next")}
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Container>
        <Modal
          isOpen={isOpenNewSubUser}
          onClose={() => setIsOpenNewSubUser(false)}
        >
          <ModalOverlay />
          <ModalContent maxW="800px">
            <ModalHeader>{t("subusers.newSubUser")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box
                display={{ base: "block", md: "flex" }}
                flexDirection="row"
                gap={4}
              >
                <Box w={{ base: "full", md: "50%" }}>
                  <VStack spacing={4} align="flex-start">
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("subusers.name")}
                      </Text>
                      <Input
                        type="text"
                        placeholder={t("subusers.name")}
                        value={newSubUserName}
                        onChange={(e) => setNewSubUserName(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("subusers.email")}
                      </Text>
                      <Input
                        type="email"
                        placeholder={t("subusers.email")}
                        value={newSubUserEmail}
                        onChange={(e) => setNewSubUserEmail(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("subusers.password")}
                      </Text>
                      <Input
                        type="password"
                        placeholder={t("subusers.password")}
                        value={newSubUserPassword}
                        onChange={(e) => setNewSubUserPassword(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("subusers.phone")}
                      </Text>
                      <Input
                        type="tel"
                        placeholder={t("subusers.phone")}
                        value={newSubUserPhone}
                        onChange={(e) => setNewSubUserPhone(e.target.value)}
                      />
                    </Box>
                  </VStack>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button onClick={handleNewSubUser}>{t("subusers.create")}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>

      <Footer />
    </Box>
  );
};
